import React, { useState } from 'react';
import '../styles/components/welcomeModal.scss';
import { AiOutlineClose } from 'react-icons/ai';
import { FiStar, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { HiOutlineCheck } from 'react-icons/hi';
import axiosClient from '../axios-client';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAfterClose?: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose, onAfterClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Track window width for responsive design
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isOpen) return null;

  const isMobile = windowWidth < 768; // Mobile breakpoint

  const handleClose = async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user._id) {
          await axiosClient.patch(`/users/${user._id}`, {
            hasSeenWelcome: true
          });
          
          const updatedUser = { ...user, hasSeenWelcome: true };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }
    } catch (error) {
      console.error('Error updating welcome modal status:', error);
    } finally {
      setIsUpdating(false);
    }
    
    onClose();
    
    if (onAfterClose) {
      setTimeout(() => {
        onAfterClose();
      }, 300);
    }
  };

  const handleNextPage = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentPage(2);
      setIsTransitioning(false);
    }, 200);
  };

  const handlePrevPage = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentPage(1);
      setIsTransitioning(false);
    }, 200);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px'
      }}
    >
      <div 
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '800px',
          height: '650px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 25px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
        }}
      >
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {currentPage === 1 ? (
            // PAGE 1 - Welcome (Original Design)
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                padding: '60px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#e6f3ff',
                    color: '#0066cc',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '24px'
                  }}>
                    <FiStar style={{ width: '16px', height: '16px' }} />
                    Welcome to QuickCruit
                  </div>
                  
                  <h1 style={{
                    fontSize: '48px',
                    fontWeight: '700',
                    lineHeight: '1.1',
                    color: '#1a1a1a',
                    margin: '0 0 20px 0'
                  }}>
                    Ready to accelerate your
                    <span style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}> job search?</span>
                  </h1>
                  
                  <p style={{
                    fontSize: '18px',
                    lineHeight: '1.6',
                    color: '#6b7280',
                    margin: '0 0 40px 0'
                  }}>
                    Join thousands of professionals who've landed their dream jobs with our AI-powered platform. 
                    Let's get you started on your journey to career success.
                  </p>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#0066cc', margin: '0 0 4px 0' }}>10K+</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Job Seekers</div>
                    </div>
                    <div style={{ width: '1px', height: '40px', backgroundColor: '#e5e7eb' }}></div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#0066cc', margin: '0 0 4px 0' }}>1500+</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Companies</div>
                    </div>
                    <div style={{ width: '1px', height: '40px', backgroundColor: '#e5e7eb' }}></div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#0066cc', margin: '0 0 4px 0' }}>96%</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Success Rate</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '32px 60px',
                borderTop: '1px solid #f3f4f6',
                backgroundColor: 'white',
                height: '112px',
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#0066cc' }}></div>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#d1d5db' }}></div>
                </div>
                
                <button 
                  onClick={handleNextPage}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '14px 28px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    border: 'none',
                    backgroundColor: '#0066cc',
                    color: 'white',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => (e.target as HTMLElement).style.backgroundColor = '#0052a3'}
                  onMouseOut={(e) => (e.target as HTMLElement).style.backgroundColor = '#0066cc'}
                >
                  Continue
                  <FiArrowRight style={{ width: '16px', height: '16px' }} />
                </button>
              </div>
            </div>
          ) : (
            // PAGE 2 - Features (Same Exact Dimensions)
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ 
                flex: 1, 
                padding: '60px',
                overflowY: 'auto',
                backgroundColor: 'white'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                  <div style={{
                    display: 'inline-block',
                    backgroundColor: '#0066cc',
                    color: 'white',
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '20px'
                  }}>
                    7-Day Free Trial
                  </div>
                  
                  <h2 style={{ 
                    fontSize: '32px', 
                    fontWeight: '700', 
                    color: '#1a1a1a', 
                    margin: '0 0 16px 0',
                    letterSpacing: '-0.02em'
                  }}>
                    Experience QuickCruit Pro
                  </h2>
                  
                  <p style={{ fontSize: '16px', color: '#6b7280', margin: '0', lineHeight: '1.6' }}>
                    Everything you need to land your next role, powered by AI
                  </p>
                </div>

                <div style={{ 
                  maxWidth: '700px', 
                  margin: '0 auto',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '40px' : '50px'
                }}>
                  {/* Premium Features */}
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        margin: '0 0 8px 0',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>Premium</h3>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>
                        AI-powered tools to maximize your success
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <HiOutlineCheck style={{ 
                          width: '20px', 
                          height: '20px', 
                          color: '#0066cc',
                          marginTop: '2px',
                          flexShrink: 0
                        }} />
                        <div>
                          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0' }}>
                            Unlimited Resume Tailoring
                          </h4>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0', lineHeight: '1.4' }}>
                            Resume optimization for every application
                          </p>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <HiOutlineCheck style={{ 
                          width: '20px', 
                          height: '20px', 
                          color: '#0066cc',
                          marginTop: '2px',
                          flexShrink: 0
                        }} />
                        <div>
                          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0' }}>
                            Unlimited Cover Letter Generation
                          </h4>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0', lineHeight: '1.4' }}>
                            Cover letters generated in seconds
                          </p>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <HiOutlineCheck style={{ 
                          width: '20px', 
                          height: '20px', 
                          color: '#0066cc',
                          marginTop: '2px',
                          flexShrink: 0
                        }} />
                        <div>
                          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0' }}>
                            Priority 24x7 Support
                          </h4>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0', lineHeight: '1.4' }}>
                            Get help when you need it most
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Free Features */}
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        color: '#1a1a1a',
                        margin: '0 0 8px 0'
                      }}>Always Free</h3>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>
                        Essential job search tools to get you started
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <HiOutlineCheck style={{ 
                          width: '20px', 
                          height: '20px', 
                          color: '#6b7280',
                          marginTop: '2px',
                          flexShrink: 0
                        }} />
                        <div>
                          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0' }}>
                            Smart Job Matching
                          </h4>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0', lineHeight: '1.4' }}>
                            Find jobs that match your skills
                          </p>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <HiOutlineCheck style={{ 
                          width: '20px', 
                          height: '20px', 
                          color: '#6b7280',
                          marginTop: '2px',
                          flexShrink: 0
                        }} />
                        <div>
                          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0' }}>
                            Application Tracker
                          </h4>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0', lineHeight: '1.4' }}>
                            Organize your job applications
                          </p>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <HiOutlineCheck style={{ 
                          width: '20px', 
                          height: '20px', 
                          color: '#6b7280',
                          marginTop: '2px',
                          flexShrink: 0
                        }} />
                        <div>
                          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0' }}>
                            Professional Portfolio
                          </h4>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0', lineHeight: '1.4' }}>
                            Showcase your achievements
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '32px 60px',
                borderTop: '1px solid #f3f4f6',
                backgroundColor: 'white',
                height: '112px',
                flexShrink: 0
              }}>
                <button 
                  onClick={handlePrevPage}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.backgroundColor = '#f9fafb';
                    target.style.borderColor = '#9ca3af';
                  }}
                  onMouseOut={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.backgroundColor = 'transparent';
                    target.style.borderColor = '#d1d5db';
                  }}
                >
                  <FiArrowLeft style={{ width: '16px', height: '16px' }} />
                  Back
                </button>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#d1d5db' }}></div>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#0066cc' }}></div>
                </div>
                
                <button 
                  onClick={handleClose} 
                  disabled={isUpdating}
                  style={{
                    backgroundColor: '#0066cc',
                    color: 'white',
                    border: 'none',
                    padding: '14px 28px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: isUpdating ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isUpdating ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => !isUpdating && ((e.target as HTMLElement).style.backgroundColor = '#0052a3')}
                  onMouseOut={(e) => !isUpdating && ((e.target as HTMLElement).style.backgroundColor = '#0066cc')}
                >
                  {isUpdating ? 'Starting...' : 'Start Free Trial'}
                  <FiArrowRight style={{ width: '16px', height: '16px' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal; 